import { Badge } from "../../../components/ui/badge";
import { formatNumber, humanize, levelTone } from "../format";
import type { ObjectionResult } from "../types";
import { Block, Empty, Quote, Stat, Unavailable, type TurnRef } from "./shared";

export function ObjectionPanel({ result, turnRef }: { result: ObjectionResult; turnRef: TurnRef }) {
  const { compliance } = result;
  const findings = compliance.prohibited_claims.length + compliance.missing_disclosures.length + compliance.policy_violations.length;

  return (
    <div className="space-y-5">
      {!result.availability.agent_prompt ? (
        <Unavailable>The agent's prompt was not available, so only scorecard rules were checked for compliance.</Unavailable>
      ) : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Risk score" value={formatNumber(result.risk_score)} hint="out of 100" />
        <Stat label="Objections" value={result.objections.length} />
        <Stat label="Compliance findings" value={findings} />
        <Stat label="Escalations" value={result.escalations.length} />
      </div>

      <Block title="Objections">
        {result.objections.length ? (
          <ul className="divide-y divide-line rounded-xl border border-line">
            {result.objections.map((o, i) => (
              <li key={i} className="space-y-1 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-ink">{humanize(o.type)}</span>
                  <Badge tone={o.resolved === true ? "success" : o.resolved === false ? "warning" : "neutral"}>
                    {o.resolved === true ? "Resolved" : o.resolved === false ? "Unresolved" : "Unclear"}
                  </Badge>
                  {o.response_quality ? <span className="text-xs text-ink-3">response {o.response_quality}</span> : null}
                </div>
                <Quote text={o.quote} turn={turnRef(o.turn_index)} />
              </li>
            ))}
          </ul>
        ) : <Empty>No objections raised.</Empty>}
      </Block>

      {result.competitors.length ? (
        <Block title="Competitors mentioned">
          <ul className="space-y-1 text-sm text-ink-2">
            {result.competitors.map((c, i) => (
              <li key={i}>{c.name} <span className="text-xs text-ink-3">({c.source})</span><Quote text={c.quote} turn={turnRef(c.turn_index)} /></li>
            ))}
          </ul>
        </Block>
      ) : null}

      <Block title="Compliance">
        {findings ? (
          <ul className="space-y-2 text-sm text-ink-2">
            {compliance.prohibited_claims.map((c, i) => (
              <li key={`p${i}`}>
                <Badge tone={levelTone(c.severity)}>{humanize(c.severity)}</Badge> Prohibited claim: {c.claim}
                <span className="ml-1 text-xs text-ink-3">(from {c.source})</span>
                <Quote text={c.quote} turn={turnRef(c.turn_index)} />
              </li>
            ))}
            {compliance.policy_violations.map((v, i) => (
              <li key={`v${i}`}>
                <Badge tone={levelTone(v.severity)}>{humanize(v.severity)}</Badge> Policy: {v.rule}
                <Quote text={v.quote} turn={turnRef(v.turn_index)} />
              </li>
            ))}
            {compliance.missing_disclosures.map((d, i) => (
              <li key={`m${i}`}>Missing disclosure: {d.disclosure} <span className="text-xs text-ink-3">(from {d.source})</span></li>
            ))}
          </ul>
        ) : <Empty>No compliance findings.</Empty>}
      </Block>

      <Block title="Escalations">
        {result.escalations.length ? (
          <ul className="space-y-1 text-sm text-ink-2">
            {result.escalations.map((e, i) => (
              <li key={i}>{humanize(e.kind)}<Quote text={e.quote} turn={turnRef(e.turn_index)} /></li>
            ))}
          </ul>
        ) : <Empty>None.</Empty>}
      </Block>
    </div>
  );
}
